import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState, selectFilters } from '@cbcore/NGRX/core.state';
import { SyndicatesScope } from '@cbcore/NGRX/syndicates/syndicates.model';
import { selectList, selectSyndicateLoading } from '@cbcore/NGRX/syndicates/syndicates.selectors';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { isEqual } from "lodash-es";
import { WidgetSyndicateListCardComponent } from '../syndicate-list-card/syndicate-list-card';
import { SlicePipe } from '@angular/common';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { PipesModule } from '@cbcore/pipes/pipes.module';

@Component({
    selector: 'widget-syndicate-list',
    templateUrl: './syndicate-list.html',
    imports: [PipesModule,
    TranslocoModule,
    CommonModule,
    
        WidgetSyndicateListCardComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        SlicePipe
    ]
})
export class WidgetSyndicateListComponent implements OnInit {
  public sortOptions: any = ['percentage_filled', 'contributors', 'cost', 'starting_soon'];
  public sortBy!: string; // Assigned unconditionally in ngOnInit's filters$ subscribe once filters emit — pre-existing assumption, not independently verified
  public syndicatesList: any = {
    items: []
  };

  public syndicateId: any;

  public loading!: boolean; // Assigned synchronously in ngOnInit's syndicatesLoading$ subscribe (NgRx selectors emit synchronously on subscribe) — pre-existing assumption, not independently verified
  public poolId!: string; // Assigned unconditionally in ngOnInit — pre-existing assumption, not independently verified

  @Output() syndicatesAvailablesEvent: EventEmitter<boolean> = new EventEmitter(true);

  private syndicatesList$!: Observable<SyndicatesScope.SyndicateList | undefined>; // Widened: selectList can return undefined; assigned unconditionally in ngOnInit
  private syndicatesLoading$!: Observable<boolean>; // Assigned unconditionally in ngOnInit — pre-existing assumption, not independently verified
  private filters$!: Observable<SyndicatesScope.Pagination>; // Assigned unconditionally in ngOnInit — pre-existing assumption, not independently verified
  private destroyRef = inject(DestroyRef);

  constructor(
    private _activatedRoute: ActivatedRoute,
    public _router: Router,
    public _cdr: ChangeDetectorRef,
    private store: Store<AppState>
  ) {
  }

  ngOnInit(): void {
    this.syndicatesLoading$ = this.store.select(selectSyndicateLoading);
    this.syndicatesLoading$.pipe(
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(loading => this.loading = loading);

    this.filters$ = this.store.select(selectFilters);
    this.filters$.pipe(
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      takeUntilDestroyed(this.destroyRef),
      filter(filters => !!filters && !!(<any>filters).sort_by)
    ).subscribe((filters) => {
      if (typeof filters.sort_by === 'object' && filters.sort_by !== null && 'join' in filters.sort_by) {
        this.sortBy = filters.sort_by.join || 'percentage_filled';
      } else if (typeof filters.sort_by === 'string') {
        this.sortBy = filters.sort_by;
      } else {
        this.sortBy = 'percentage_filled';
      }
      this._cdr.markForCheck();
    });

    this.syndicatesList$ = this.store.select(selectList);
    this.syndicatesList$.pipe(
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(syndicateList => this.updateData(syndicateList));

    this._activatedRoute.params.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      this.poolId = params.pool_id;
    });
  }

  updateData(data: any): void {
    if (data && data.items.length) {
      this.syndicatesList = data;
    }
    if (data && data.items.length < 2) {
      this.syndicatesAvailablesEvent.emit(false);
    }
  }

  openPoolSyndicates(syndicateId?: number): void {
    syndicateId ?
      this._router.navigate(['/', 'syndicates', 'join', 'pool', this.poolId, syndicateId])
      :
      this._router.navigate(['/', 'pools', this.poolId, 'join']);
  }
}
